export const qrCodeLanding = {
  seo: {
    title: 'Free QR Code Generator | Static QR Codes | PixelUtility',
    description:
      'Create static QR codes for WiFi, URLs, business cards, restaurants, and events. No tracking redirects. Download PNG or SVG free in your browser.',
  },
  hero: {
    title: 'Free QR Code Generator',
    subtitle:
      'Generate clean static QR codes without redirect pages or forced tracking. Perfect for WiFi sharing, menus, business cards, and print marketing.',
    accent: 'emerald',
  },
  features: [
    { title: 'Static QR Codes', description: 'Content is encoded directly in the image — no middleman redirect URL required.' },
    { title: 'WiFi QR', description: 'Guests scan once to join your network without typing passwords.' },
    { title: 'PNG & SVG Export', description: 'Raster for slides and social; vector SVG for professional print.' },
    { title: 'No Account', description: 'Generate and download immediately.' },
    { title: 'Print-Ready Sizing', description: 'Control quiet zone and module size for posters, tables, and packaging.' },
    { title: 'Privacy-Focused', description: 'No forced analytics redirect layer on your QR destination.' },
  ],
  examples: {
    title: 'QR Examples Gallery',
    subtitle: 'Common QR types businesses and individuals create every day.',
    items: [
      { name: 'WiFi QR', description: 'SSID and password encoded for cafés, Airbnbs, and offices.' },
      { name: 'Restaurant Menu QR', description: 'Link to PDF or web menu on table tents and window stickers.' },
      { name: 'Business Card QR', description: 'vCard or LinkedIn URL on cards and email signatures.' },
      { name: 'Event QR', description: 'Ticket links, RSVP forms, or calendar add links for conferences.' },
      { name: 'Payment QR', description: 'PayPal, Venmo, or crypto address strings where supported.' },
      { name: 'App Download QR', description: 'App Store or Play Store URLs on posters and packaging.' },
    ],
  },
  useCases: [
    'Retail window displays linking to weekly promotions',
    'Real estate yard signs with property detail pages',
    'Trade show booths with lead capture forms',
    'Product packaging for manuals and warranty registration',
    'Healthcare waiting rooms with intake forms (HIPAA workflows permitting)',
    'Museum exhibits with audio guide URLs',
  ],
  howItWorks: [
    { title: 'Select QR type', description: 'Choose URL, text, WiFi, or other supported payload types.' },
    { title: 'Enter your content', description: 'Paste a link, network credentials, or contact details with validation hints.' },
    { title: 'Customize appearance', description: 'Adjust size, margin, and error correction for your print or screen use case.' },
    { title: 'Download PNG or SVG', description: 'Save the file and place it in your design — no account or watermark required.' },
  ],
  comparisonLinks: [
    { to: '/blog/static-vs-dynamic-qr-codes', label: 'Static vs dynamic QR codes — full comparison' },
    { to: '/blog/restaurant-qr-menu-ideas', label: 'Restaurant QR menu ideas that work' },
  ],
  faq: [
    { q: 'Are PixelUtility QR codes free?', a: 'Yes. Generating and downloading static QR codes is free. Open qr-code.pixelutility.com from the Launch Tool button for the full editor.' },
    { q: 'What is the difference between static and dynamic QR codes?', a: 'Static QR codes embed the final URL or data directly. Dynamic codes point to a short redirect URL that can be changed later and often includes click analytics. Static codes last as long as the encoded destination exists.' },
    { q: 'Are QR codes permanent?', a: 'The image itself does not expire. If you encode a URL and later delete that page, scans will fail. Dynamic services let you change the destination without reprinting — at the cost of depending on their redirect server.' },
    { q: 'What size should a QR code be for print?', a: 'A common minimum is 2 cm × 2 cm (about 0.8 inch) for close-range scanning. Larger sizes help outdoor signage and older phone cameras. Always test print proofs.' },
    { q: 'Can I create a WiFi QR code?', a: 'Yes. WiFi QR encodes SSID, encryption type, and password so Android and iOS devices can offer to join the network after scanning.' },
    { q: 'Do QR codes work without internet?', a: 'WiFi and plain text codes can work offline. URL codes need connectivity to load the destination.' },
    { q: 'PNG or SVG for business cards?', a: 'SVG is best for vector print workflows. PNG at 300 DPI equivalent pixel dimensions works for most card printers.' },
    { q: 'Will my QR code track users?', a: 'Static codes do not inherently add a tracking redirect. Analytics depend on what you link to (e.g., your website with consent-based analytics).' },
    { q: 'Can I put a logo in the center?', a: 'Yes, with sufficient error correction (often level Q or H). Test scanning after adding branding.' },
    { q: 'Are QR codes still relevant in 2026?', a: 'Yes — menus, payments, authentication, and packaging use cases continue to grow globally.' },
    { q: 'Is this page just a redirect?', a: 'No. This is a full guide on QR technology, use cases, and best practices. The generator opens separately when you choose Launch Tool.' },
    { q: 'What marketing ideas work with QR?', a: 'Pair codes with clear calls to action ("Scan for 10% off"), place at eye level, and ensure mobile landing pages load under three seconds.' },
  ],
  longContent: [
    {
      id: 'static-dynamic',
      title: 'Static vs Dynamic QR Codes',
      paragraphs: [
        'Businesses choosing QR technology should decide whether the encoded destination might change after printing. Static QR codes are ideal when the URL, WiFi network, or vCard is stable for months or years. You print once; scans go directly to the content. Dynamic QR codes route through a provider\'s domain so marketing teams can swap campaigns without reissuing posters — useful for seasonal promotions but dependent on that vendor\'s uptime and pricing.',
        'PixelUtility focuses on static generation because many small businesses want simplicity, no subscription, and no redirect that could break if a SaaS account lapses. Marketing teams running A/B tests may still prefer dynamic providers — compare total cost of ownership including annual fees.',
      ],
    },
    {
      id: 'permanent',
      title: 'Are QR Codes Permanent?',
      paragraphs: [
        'The black-and-white pattern does not degrade digitally. Permanence of the user experience depends on what you encode. Broken links, renamed PDFs, and expired event pages cause "dead" QR experiences. Archive redirects on your own domain when you must change URLs, and update printed materials for high-value campaigns.',
        'For restaurant menus, link to a stable menu page you control rather than a third-party folder link that might change permissions. Use HTTPS everywhere to avoid browser warnings.',
      ],
    },
    {
      id: 'marketing',
      title: 'QR Code Marketing Ideas That Convert',
      paragraphs: [
        'Successful campaigns tell users what they get after scanning: a discount, menu, installation guide, or warranty activation. Place codes where people naturally pause — tables, checkout counters, packaging inserts, not blurry backgrounds. Contrast matters: dark modules on light backgrounds scan best.',
        'Track performance through your landing page analytics (with cookie consent where required) instead of opaque redirect analytics when possible. A/B test headline copy on the destination page, not just QR placement.',
      ],
    },
    {
      id: 'print-sizes',
      title: 'QR Code Sizes for Print and Signage',
      paragraphs: [
        'Scan distance drives size. Business cards: 0.8–1 inch. Posters viewed at 2 meters: 2–4 inches or larger. Billboards need much larger codes relative to viewing distance. Maintain quiet zone (blank margin) around the code per ISO standards.',
        'Export SVG for large-format print to avoid pixelation. For office documents, PNG at sufficient resolution is fine. Always print a test sheet and scan with both iPhone and Android devices before mass production.',
      ],
    },
    {
      id: 'wifi-restaurant',
      title: 'WiFi, Restaurant, and Event QR Use Cases',
      paragraphs: [
        'WiFi QR reduces support burden in hospitality and coworking spaces. Rotate passwords? Generate a new code and replace table tents. Restaurant menu QR surged as a hygiene-friendly option; ensure the mobile page is accessible (font size, contrast, dietary filters).',
        'Event QR can deep-link to calendar files (.ics) or registration. Conference badges often encode attendee vCards — keep payloads concise to maintain low-density modules that scan faster.',
      ],
    },
    {
      id: 'hub-page',
      title: 'Why This Landing Page Exists on pixelutility.com',
      paragraphs: [
        'Search engines and ad programs reward substantive publisher content adjacent to tools. This page teaches QR concepts, compares technologies, and links to related blog articles — while the interactive generator lives on qr-code.pixelutility.com. That separation keeps the main domain as an SEO and content hub without thin doorway pages.',
      ],
    },
    {
      id: 'security',
      title: 'QR Code Security and Phishing Awareness',
      paragraphs: [
        'Because scanning is frictionless, attackers place malicious QR stickers over legitimate ones in parking lots and public transport. Businesses should educate staff to preview URLs before opening on corporate devices. Use HTTPS destinations and branded short domains you control.',
        'For customer-facing codes, register the final domain in your style guide so marketing vendors cannot swap redirect providers without approval.',
      ],
    },
    {
      id: 'accessibility',
      title: 'Accessibility and Inclusive QR Design',
      paragraphs: [
        'Pair QR codes with human-readable URLs for users who cannot scan. Ensure sufficient contrast (dark on light). Provide alternative access paths on restaurant menus — printed summaries or staff assistance — for guests without smartphones.',
      ],
    },
  ],
};
